import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Star, Heart, Sparkles, Save, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fun question options for middle schoolers
const favoriteSubjects = ['Math', 'Science', 'English', 'History', 'Art', 'Music', 'PE', 'Computer'];
const funActivities = ['Sports', 'Video Games', 'Reading', 'Art/Drawing', 'Music', 'Hanging with Friends', 'Watching YouTube', 'Building Things'];
const superPowers = ['Super Smart', 'Super Creative', 'Super Friendly', 'Super Athletic', 'Super Helpful', 'Super Funny', 'Super Brave', 'Super Patient'];

export default function MSAboutMePage() {
  const { user, token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nickname: '',
    favoriteSubject: '',
    leastFavoriteSubject: '',
    funActivity: '',
    superPower: '',
    dreamJob: '',
    funFact: ''
  });

  useEffect(() => {
    // Load existing data if available
    if (user) {
      setFormData(prev => ({
        ...prev,
        nickname: user.nickname || user.full_name?.split(' ')[0] || '',
        favoriteSubject: user.favorite_subject || '',
        leastFavoriteSubject: user.least_favorite_subject || '',
        funActivity: user.fun_activity || '',
        superPower: user.super_power || '',
        dreamJob: user.dream_job || '',
        funFact: user.fun_fact || ''
      }));
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, {
        nickname: formData.nickname,
        favorite_subject: formData.favoriteSubject,
        least_favorite_subject: formData.leastFavoriteSubject,
        fun_activity: formData.funActivity,
        super_power: formData.superPower,
        dream_job: formData.dreamJob,
        fun_fact: formData.funFact
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Awesome! Your profile is saved! 🎉');
    } catch (error) {
      toast.error('Oops! Something went wrong. Try again!');
    } finally {
      setSaving(false);
    }
  };

  const SelectButton = ({ options, value, onChange, color = 'blue' }) => {
    const colorMap = {
      blue: 'from-blue-500 to-cyan-500',
      pink: 'from-pink-500 to-rose-500',
      amber: 'from-amber-500 to-orange-500',
      slate: 'from-slate-500 to-gray-500',
      purple: 'from-purple-500 to-indigo-500',
      green: 'from-green-500 to-emerald-500'
    };
    return (
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              value === option
                ? `bg-gradient-to-r ${colorMap[color] || colorMap.blue} text-white shadow-lg`
                : 'bg-gradient-to-r from-slate-700 to-slate-600 text-white hover:from-slate-600 hover:to-slate-500'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Who Am I?</h1>
            <p className="text-slate-300">Tell us about yourself!</p>
          </div>
        </div>
      </motion.div>

      <div className="space-y-6">
        {/* Nickname */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30"
        >
          <Label className="text-white mb-2 flex items-center gap-2 text-lg font-semibold">
            <Star className="w-5 h-5 text-yellow-400" />
            What should we call you?
          </Label>
          <Input
            placeholder="Your nickname..."
            value={formData.nickname}
            onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
            className="input-dark mt-2"
            data-testid="nickname-input"
          />
        </motion.div>

        {/* Favorite Subject */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-pink-500/20 to-rose-500/20 border border-pink-500/30"
        >
          <Label className="text-white mb-3 flex items-center gap-2 text-lg font-semibold">
            <Heart className="w-5 h-5 text-pink-400" />
            What's your FAVORITE subject in school?
          </Label>
          <SelectButton
            options={favoriteSubjects}
            value={formData.favoriteSubject}
            onChange={(val) => setFormData({ ...formData, favoriteSubject: val })}
            color="pink"
          />
        </motion.div>

        {/* Least Favorite Subject */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-slate-500/20 to-gray-500/20 border border-slate-500/30"
        >
          <Label className="text-white mb-3 flex items-center gap-2 text-lg font-semibold">
            😅 What subject is... not your favorite?
          </Label>
          <SelectButton
            options={favoriteSubjects}
            value={formData.leastFavoriteSubject}
            onChange={(val) => setFormData({ ...formData, leastFavoriteSubject: val })}
            color="slate"
          />
        </motion.div>

        {/* Fun Activity */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30"
        >
          <Label className="text-white mb-3 flex items-center gap-2 text-lg font-semibold">
            🎮 What do you like doing for fun?
          </Label>
          <SelectButton
            options={funActivities}
            value={formData.funActivity}
            onChange={(val) => setFormData({ ...formData, funActivity: val })}
            color="blue"
          />
        </motion.div>

        {/* Super Power */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30"
        >
          <Label className="text-white mb-3 flex items-center gap-2 text-lg font-semibold">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            What's YOUR superpower?
          </Label>
          <SelectButton
            options={superPowers}
            value={formData.superPower}
            onChange={(val) => setFormData({ ...formData, superPower: val })}
            color="amber"
          />
        </motion.div>

        {/* Dream Job */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30"
        >
          <Label className="text-white mb-2 flex items-center gap-2 text-lg font-semibold">
            🚀 If you could have ANY job, what would it be?
          </Label>
          <Input
            placeholder="Astronaut, YouTuber, Doctor, Artist..."
            value={formData.dreamJob}
            onChange={(e) => setFormData({ ...formData, dreamJob: e.target.value })}
            className="input-dark mt-2"
            data-testid="dream-job-input"
          />
        </motion.div>

        {/* Fun Fact */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30"
        >
          <Label className="text-white mb-2 flex items-center gap-2 text-lg font-semibold">
            🎯 One fun fact about you:
          </Label>
          <Input
            placeholder="I can solve a Rubik's cube, I have a pet lizard..."
            value={formData.funFact}
            onChange={(e) => setFormData({ ...formData, funFact: e.target.value })}
            className="input-dark mt-2"
            data-testid="fun-fact-input"
          />
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3"
            data-testid="save-about-me"
          >
            {saving ? (
              'Saving...'
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save My Profile!
              </>
            )}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
